/**
 * Durable anonymous visitor id for LLM referral attribution.
 *
 * WHAT IT IS: a random UUID in localStorage. It identifies a BROWSER so an
 * LLM-referred landing can later be joined to a contact submission, an audit,
 * an unlock or a purchase. It is not an identity — the visitor stays anonymous
 * unless they submit a form, sign in or buy, and nothing derived from the
 * person (IP, fingerprint, email) ever feeds into it.
 *
 * Consent-gated on the same central resolver GA4/Clarity/PostHog use: no id is
 * minted or returned unless analytics are currently allowed, and consent
 * withdrawal clears it (wired in AnalyticsGate alongside clearFirstTouchUtm).
 * A non-consenting visitor is simply never attributed.
 *
 * Storage mirrors first-touch-utm.ts deliberately: one namespaced key, an
 * in-memory fallback so a blocked/unavailable localStorage never throws, and a
 * consent-aware read that clears rather than returning a stale id.
 *
 * PostHog's `distinct_id` is deliberately NOT reused. That client runs
 * person_profiles:'identified_only' and never calls identify(); borrowing its
 * id would couple this table to an anonymity model that is sealed and could
 * imply person-level tracking there. This id is ours and server-persisted.
 */

import { isAnalyticsAllowed } from '@/lib/consent-manager'

const STORAGE_KEY = 'pm_visitor_id'

// Canonical v4 UUID. Anything else in storage is treated as corrupt and
// replaced, so a hand-edited or truncated value cannot reach the API.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

let memoryId: string | null = null

/**
 * Consent check that can never throw.
 *
 * These functions are called INLINE while building a contact payload, an audit
 * request and a Paddle checkout. If the consent resolver ever threw, an
 * unguarded call would take the conversion down with it — and attribution must
 * never block a conversion. Failing closed (treat as not allowed) is the safe
 * direction: worst case an attribution row is missed.
 */
function consentAllowed(): boolean {
  try {
    return isAnalyticsAllowed() === true
  } catch {
    return false
  }
}

function safeGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function safeSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Held in memory for this session only.
  }
}

function mintUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch {
    // Fall through to the manual path.
  }
  // Fallback for older/embedded contexts without randomUUID.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * The current visitor id, minting one on first consented call.
 *
 * Returns null when analytics are not allowed — an explicit denial, an
 * undecided prior-consent region, or a lapsed consent record — and clears any
 * existing id so stale attribution can never be read or forwarded.
 */
export function getOrCreateVisitorId(): string | null {
  if (typeof window === 'undefined') return null
  if (!consentAllowed()) {
    clearVisitorId()
    return null
  }

  const stored = safeGet()
  if (stored && UUID_RE.test(stored)) return stored
  if (memoryId && UUID_RE.test(memoryId)) return memoryId

  try {
    const minted = mintUuid()
    memoryId = minted
    safeSet(minted)
    return minted
  } catch {
    // No crypto available — attribution is skipped, never fatal.
    return null
  }
}

/**
 * The visitor id WITHOUT minting one. For conversion call sites that should
 * attribute an existing visitor but must never create attribution as a side
 * effect of a form submit or a checkout.
 */
export function peekVisitorId(): string | null {
  if (typeof window === 'undefined') return null
  if (!consentAllowed()) return null
  const stored = safeGet()
  if (stored && UUID_RE.test(stored)) return stored
  return memoryId && UUID_RE.test(memoryId) ? memoryId : null
}

/** Called on consent withdrawal. */
export function clearVisitorId(): void {
  memoryId = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}
